import { useState, useEffect, useCallback } from 'react'
import api from '../lib/axios'

/**
 * useTestPlanRule
 *
 * Manages the test plan generation rule for a single repository.
 * Handles load, save, manual trigger, and history.
 */
export function useTestPlanRule(repositoryId) {
  const [rule, setRule]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)

  const load = useCallback(async () => {
    if (!repositoryId) return
    setLoading(true)
    try {
      const { data } = await api.get(`/repositories/${repositoryId}/test-plan-rule`)
      setRule(data)
    } catch {
      setError('Failed to load test plan generation settings.')
    } finally {
      setLoading(false)
    }
  }, [repositoryId])

  useEffect(() => { load() }, [load])

  const save = async (updates) => {
    setSaving(true)
    setError(null)
    try {
      const { data } = await api.put(`/repositories/${repositoryId}/test-plan-rule`, updates)
      setRule(data)
      return { ok: true }
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to save rule.'
      setError(msg)
      return { ok: false, error: msg, errors: err.response?.data?.errors }
    } finally {
      setSaving(false)
    }
  }

  const trigger = async ({ branch, coverageTypes } = {}) => {
    try {
      const { data } = await api.post(`/repositories/${repositoryId}/test-plan-rule/trigger`, {
        branch,
        coverage_types: coverageTypes,
      })
      await load() // refresh rule (trigger_count, last_triggered_at)
      return data
    } catch (err) {
      return {
        triggered: false,
        reason: err.response?.data?.message ?? 'Trigger failed.',
      }
    }
  }

  return { rule, loading, saving, error, save, trigger, reload: load }
}

/**
 * useTestPlanHistory
 *
 * Loads the generation history for a repository.
 */
export function useTestPlanHistory(repositoryId) {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!repositoryId) return
    try {
      const { data } = await api.get(`/repositories/${repositoryId}/test-plan-rule/history`)
      setHistory(data)
    } finally {
      setLoading(false)
    }
  }, [repositoryId])

  useEffect(() => { load() }, [load])

  return { history, loading, reload: load }
}
